'use client';

import React, { createContext, useContext, useMemo } from 'react';
import Link, { type LinkProps } from 'next/link';

// Types
type LegalSectionDef = {
  id: string;
  title: string;
  number?: string | number;
};

type LegalReference = {
  id: string;
  label: string;
  href?: string;
};

type LegalDocumentProps = {
  title: string;
  updated?: string;
  sections?: LegalSectionDef[];
  references?: LegalReference[];
  children: React.ReactNode;
  aside?: React.ReactNode;
};

type LegalContextType = {
  referencesMap: Map<string, number>;
  references: LegalReference[];
};

const LegalContext = createContext<LegalContextType | null>(null);

const buildReferencesMap = (items: LegalReference[]): Map<string, number> => {
  const map = new Map<string, number>();
  for (const [index, ref] of items.entries()) {
    map.set(ref.id, index + 1);
  }
  return map;
};

function useLegalContext() {
  const ctx = useContext(LegalContext);
  if (!ctx) {
    throw new Error('Legal components must be used within <Legal.Document>');
  }
  return ctx;
}

// Layout component with sticky TOC and bespoke Tailwind utilities
function Document({
  title,
  updated,
  sections = [],
  references = [],
  children,
  aside,
}: Readonly<LegalDocumentProps>) {
  const referencesMap = useMemo(() => buildReferencesMap(references), [references]);

  const contextValue = useMemo(
    () => ({ referencesMap, references }),
    [referencesMap, references],
  );

  return (
    <LegalContext.Provider value={contextValue}>
      <main className="py-16 sm:py-20 lg:py-24 px-6 sm:px-8 lg:px-12 text-neutral-900 dark:text-neutral-600">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Floating/utility aside */}
            {aside ? (
              <aside className="hidden lg:block lg:col-span-2">
                <div className="sticky top-24 space-y-6">{aside}</div>
              </aside>
            ) : null}

            {/* Main content */}
            <div
              className={
                aside ? 'lg:col-span-7' : 'lg:col-span-8 lg:col-start-3'
              }
            >
              {/* Mobile back link, shown when aside exists */}
              {aside ? (
                <div className="lg:hidden mb-4 opacity-100">{aside}</div>
              ) : null}
              <header className="mb-10">
                <h1 className="text-3xl/tight sm:text-4xl/tight font-semibold tracking-tight text-neutral-950 dark:text-neutral-100 opacity-100">
                  {title}
                </h1>
                {updated ? (
                  <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                    Last updated: {updated}
                  </p>
                ) : null}
              </header>
              <article className="max-w-none text-neutral-900 dark:text-neutral-100">
                {children}
              </article>
              {references.length > 0 ? (
                <ReferencesList className="mt-14" />
              ) : null}
            </div>

            {/* TOC */}
            {sections.length > 0 ? (
              <nav
                aria-label="Table of contents"
                className="hidden lg:block lg:col-span-3"
              >
                <div className="sticky top-24 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-900/70 backdrop-blur-sm p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-3">
                    On this page
                  </p>
                  <ol className="space-y-2 text-sm">
                    {sections.map((s) => (
                      <li key={s.id} className="group">
                        <a
                          href={`#${s.id}`}
                          className="flex gap-2 text-neutral-800 dark:text-neutral-200 hover:text-neutral-950 dark:hover:text-white"
                        >
                          {s.number ? (
                            <span className="w-6 shrink-0 text-neutral-500 group-hover:text-neutral-700 dark:text-neutral-400 dark:group-hover:text-neutral-200">
                              {s.number}
                            </span>
                          ) : null}
                          <span>{s.title}</span>
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>
              </nav>
            ) : null}
          </div>
        </div>
      </main>
    </LegalContext.Provider>
  );
}

type SectionProps = {
  id: string;
  title: string;
  number?: string | number;
  level?: 2 | 3 | 4;
  children: React.ReactNode;
};

// Heading with self-link anchor and fine-grained spacing
const headingTagByLevel: Record<
  Required<SectionProps>['level'],
  React.ElementType
> = {
  2: 'h2',
  3: 'h3',
  4: 'h4',
};

function Section({ id, title, number, level = 2, children }: Readonly<SectionProps>) {
  const HeadingTag = headingTagByLevel[level] ?? 'h2';

  return (
    <section id={id} className="scroll-mt-28">
      <HeadingTag className="group relative mt-10 mb-3 text-xl sm:text-2xl font-semibold text-neutral-950 dark:text-neutral-600 opacity-100">
        <a
          href={`#${id}`}
          className="absolute -left-6 top-1 opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Copy link"
        >
          <span className="text-neutral-300 group-hover:text-neutral-500 dark:text-neutral-600 dark:group-hover:text-neutral-400">
            #
          </span>
        </a>
        {number ? (
          <span className="mr-2 text-neutral-700 dark:text-neutral-600">
            {number}.
          </span>
        ) : null}
        {title}
      </HeadingTag>
      <div className="space-y-4 text-neutral-900 dark:text-neutral-100 leading-relaxed [&>ul]:text-neutral-900 [&>ul]:marker:text-neutral-600 [&>ol]:text-neutral-900 [&>ol]:marker:text-neutral-600 [&>dl]:text-neutral-900 [&>dl>div>dd]:text-neutral-900">
        {children}
      </div>
    </section>
  );
}

type CalloutProps = {
  title?: string;
  tone?: 'info' | 'success' | 'warning' | 'danger' | 'neutral';
  children: React.ReactNode;
};

function toneStyles(tone: CalloutProps['tone']) {
  switch (tone) {
    case 'success':
      return 'border-emerald-300/60 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/50 dark:text-emerald-200';
    case 'warning':
      return 'border-amber-300/60 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/50 dark:text-amber-200';
    case 'danger':
      return 'border-rose-300/60 bg-rose-50 text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/50 dark:text-rose-200';
    case 'neutral':
      return 'border-neutral-200 bg-neutral-50 text-neutral-800 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200';
    case 'info':
    default:
      return 'border-sky-300/60 bg-sky-50 text-sky-900 dark:border-sky-900/60 dark:bg-sky-950/50 dark:text-sky-200';
  }
}

function Callout({ title, tone = 'info', children }: Readonly<CalloutProps>) {
  return (
    <div className={`my-6 rounded-xl border px-4 py-3 ${toneStyles(tone)}`}>
      {title ? <p className="font-semibold mb-1">{title}</p> : null}
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

type BreakProps = {
  summary: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
};

function Break({ summary, children, defaultOpen }: Readonly<BreakProps>) {
  return (
    <details
      className="my-4 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4 bg-white/90 dark:bg-neutral-900/60"
      open={defaultOpen}
    >
      <summary className="cursor-pointer select-none font-semibold text-neutral-900 dark:text-neutral-100 marker:content-['']">
        {summary}
      </summary>
      <div className="mt-2 text-sm text-neutral-900 dark:text-neutral-100 space-y-2">
        {children}
      </div>
    </details>
  );
}

type FootnoteProps = {
  refId: string;
};

function Footnote({ refId }: Readonly<FootnoteProps>) {
  const { referencesMap } = useLegalContext();
  const index = referencesMap.get(refId);
  if (!index) return null;
  return (
    <sup id={refId} className="align-super text-xs">
      <a
        href={`#ref-${refId}`}
        className="text-sky-700 dark:text-sky-400 hover:underline"
        aria-label={`See reference ${index}`}
      >
        [{index}]
      </a>
    </sup>
  );
}

function ReferencesList({ className = '' }: Readonly<{ className?: string }>) {
  const { references } = useLegalContext();
  if (!references.length) return null;
  return (
    <aside
      className={`border-t border-neutral-200 dark:border-neutral-800 pt-6 ${className}`}
      aria-labelledby="references-heading"
    >
      <h2
        id="references-heading"
        className="text-base font-semibold text-neutral-950 dark:text-neutral-50 mb-3"
      >
        References
      </h2>
      <ol className="space-y-2 text-sm text-neutral-900 dark:text-neutral-200 list-decimal list-outside pl-5">
        {references.map((r) => (
          <li key={r.id} id={`ref-${r.id}`} className="scroll-mt-28">
            {r.href ? (
              <a
                href={r.href}
                target="_blank"
                rel="noreferrer"
                className="hover:underline text-sky-700 dark:text-sky-300"
              >
                {r.label}
              </a>
            ) : (
              <span>{r.label}</span>
            )}
            <a
              href={`#${r.id}`}
              className="ml-2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
              aria-label="Back to content"
            >
              ↑
            </a>
          </li>
        ))}
      </ol>
    </aside>
  );
}

const linkBaseClass = 'underline decoration-dotted underline-offset-2';

type NextLinkOptionalProps = Pick<
  LinkProps,
  'prefetch' | 'replace' | 'scroll' | 'shallow' | 'passHref' | 'locale'
>;

type LegalLinkProps = {
  href: LinkProps['href'];
  className?: string;
  children: React.ReactNode;
} & NextLinkOptionalProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

const httpPattern = /^(https?:)?\/\//;
const mailTelPattern = /^(mailto:|tel:)/;
const absoluteSchemePattern = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

function isHashHref(href: string) {
  return href.startsWith('#');
}

function isHttpHref(href: string) {
  return httpPattern.test(href);
}

function isMailOrTelHref(href: string) {
  return mailTelPattern.test(href);
}

function isAbsoluteNonHttpScheme(href: string) {
  return (
    absoluteSchemePattern.test(href) &&
    !isHttpHref(href) &&
    !isMailOrTelHref(href)
  );
}

// A small helper to render internal/external links consistently within legal text
function A({
  href,
  children,
  className = '',
  prefetch,
  replace,
  scroll,
  shallow,
  passHref,
  locale,
  ...anchorProps
}: LegalLinkProps) {
  const combinedClassName = className
    ? `${linkBaseClass} ${className}`
    : linkBaseClass;

  if (typeof href === 'string') {
    if (
      isHttpHref(href) ||
      isMailOrTelHref(href) ||
      isHashHref(href) ||
      isAbsoluteNonHttpScheme(href)
    ) {
      const externalAttributes = isHttpHref(href)
        ? ({ target: '_blank', rel: 'noreferrer' } satisfies Pick<
            React.AnchorHTMLAttributes<HTMLAnchorElement>,
            'target' | 'rel'
          >)
        : {};

      return (
        <a
          href={href}
          className={combinedClassName}
          {...externalAttributes}
          {...anchorProps}
        >
          {children}
        </a>
      );
    }
  }

  return (
    <Link
      href={href}
      prefetch={prefetch}
      replace={replace}
      scroll={scroll}
      shallow={shallow}
      passHref={passHref}
      locale={locale}
      className={combinedClassName}
      {...anchorProps}
    >
      {children}
    </Link>
  );
}

export const Legal = {
  Document,
  Section,
  Callout,
  Break,
  Footnote,
  ReferencesList,
  A,
};

export type { LegalSectionDef, LegalReference };

// Also export individually for direct imports
export { Document as LegalDocument };
export { Section as LegalSection };
export { Callout as LegalCallout };
export { Break as LegalBreak };
export { Footnote as LegalFootnote };
export { ReferencesList as LegalReferencesList };
export { A as LegalLink };
